import { AlertCircle, Camera, CheckCircle2, CloudUpload, ImageIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useUploadsForJob } from '@/hooks/upload-queue';
import { formatInstant } from '@/lib/dates';
import type { QueuedUpload } from '@/lib/upload-queue';
import type { EmployeeJobDetail, EmployeeJobTaskView } from '@shared/schemas/employee-portal';

/*
 * **The photo-gated checklist. This is the core of the whole application** —
 * business-rules.md §3.1, FINDINGS.md §11.2, and the rule the Airo roadmap
 * missed completely.
 *
 * **There is no checkbox on this screen and there must never be one.** Where a
 * task requires a photo — which is the default — uploading the first photo *is*
 * the completion event. The server owns that: `recordPhotoUpload` sets
 * `is_completed` itself, `setTaskCompletion` refuses to, and there is no
 * employee endpoint that ticks a task off at all. So this file has nothing to
 * toggle; it has an Add photo control and a caption saying what that control
 * does.
 *
 * Three things the prototype gets right and this keeps:
 *
 *   - **The banner counts photos, at the top, before any task is read.** Nobody
 *     should discover the requirement at the end of a three-hour clean.
 *   - **Every task is marked `Required`**, visibly, not implied.
 *   - **The progress bar is labelled "based on photos uploaded"**, because a
 *     progress bar that does not say what it measures is a progress bar people
 *     guess at.
 *
 * One thing it gets wrong and this fixes: **"Add photo" is 99×30px at 1.34:1**
 * in the prototype (P23) — under the 44×44 floor, in yellow-on-white that is
 * effectively invisible, and it is the single most-tapped control in the
 * business. Here it is a `Button` at 44px minimum with `bark-700` text, which
 * the token contrast test keeps honest.
 */

export function TaskChecklist({
  detail,
  onAddPhoto,
}: {
  detail: EmployeeJobDetail;
  onAddPhoto: (task: EmployeeJobTaskView) => void;
}) {
  const queued = useUploadsForJob(detail.id);
  const { photoRequiredTotal, photoRequiredCompleted } = detail.progress;
  const percent =
    photoRequiredTotal === 0 ? 0 : Math.round((photoRequiredCompleted / photoRequiredTotal) * 100);

  if (detail.tasks.length === 0) {
    return (
      <section className="rounded-lg border border-ink-300 bg-white p-4 shadow-card">
        <h2 className="text-h3 text-ink-900">Task checklist</h2>
        <p className="mt-2 text-body text-ink-500">
          {/* A fact and what it means, not an empty box. The category carries the
              template, and a job created without one has no checklist to copy. */}
          This job has no checklist. Clean to the usual standard and photograph anything worth
          recording.
        </p>
      </section>
    );
  }

  return (
    <section className="rounded-lg border border-ink-300 bg-white shadow-card">
      {/*
        The header stacks rather than sitting the counters beside the title. At
        375px the prototype's version wraps awkwardly and the two counters crowd
        the heading (P28) — a column costs nothing and cannot crowd anything.
      */}
      <div className="border-b border-ink-300 p-4">
        <h2 className="text-h3 text-ink-900">Task checklist</h2>
        {detail.categoryName ? (
          <p className="mt-0.5 text-meta text-ink-500">{detail.categoryName}</p>
        ) : null}

        {photoRequiredTotal > 0 ? (
          <div className="mt-3 rounded-sm bg-honey-50 p-3">
            <p className="text-label text-bark-900">
              {/* The prototype's own sentence, and it is the right one. */}
              {photoRequiredCompleted}/{photoRequiredTotal} task photos uploaded
            </p>
            <p className="mt-1 text-meta text-ink-700">
              Each task needs one photo before it can be checked off. Upload a photo for each task
              below.
            </p>

            <div
              className="mt-2.5 h-2 overflow-hidden rounded-full bg-white"
              role="progressbar"
              aria-valuenow={photoRequiredCompleted}
              aria-valuemin={0}
              aria-valuemax={photoRequiredTotal}
              aria-label="Task photos uploaded"
            >
              <div
                className="h-full rounded-full bg-honey-400 transition-[width]"
                style={{ width: `${String(percent)}%` }}
              />
            </div>
            <p className="mt-1.5 text-meta text-ink-500">Progress based on photos uploaded</p>
          </div>
        ) : null}
      </div>

      <ul>
        {detail.tasks.map((task) => (
          <TaskRow
            key={task.id}
            task={task}
            canUpload={detail.canUploadPhotos}
            queued={queued.filter((entry) => entry.jobTaskId === task.id)}
            onAddPhoto={() => {
              onAddPhoto(task);
            }}
          />
        ))}
      </ul>
    </section>
  );
}

function TaskRow({
  task,
  canUpload,
  queued,
  onAddPhoto,
}: {
  task: EmployeeJobTaskView;
  canUpload: boolean;
  /** Photos taken for this task that have not landed on the server yet. */
  queued: QueuedUpload[];
  onAddPhoto: () => void;
}) {
  const inFlight = queued.filter((entry) => !entry.exhausted).length;
  const stuck = queued.filter((entry) => entry.exhausted);

  return (
    <li className="border-b border-ink-300 p-4 last:border-b-0">
      <div className="flex items-start gap-3">
        {/*
          A status *icon*, not a checkbox. It is not interactive and it must not
          look interactive: the way to complete this task is the button below.
        */}
        <span aria-hidden="true" className="mt-0.5 shrink-0">
          {task.isCompleted ? (
            <CheckCircle2 className="size-5 text-status-completed-text" />
          ) : (
            <span className="block size-5 rounded-full border-2 border-ink-300" />
          )}
        </span>

        <div className="min-w-0 flex-1">
          <p className="text-body text-ink-900">{task.name}</p>

          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
            {task.isRequired ? (
              <span className="rounded-full bg-ink-100 px-2 py-0.5 text-meta text-ink-700">
                Required
              </span>
            ) : (
              <span className="rounded-full bg-ink-100 px-2 py-0.5 text-meta text-ink-700">
                Optional
              </span>
            )}
            {task.photoCount > 0 ? (
              <span className="inline-flex items-center gap-1 text-meta text-ink-500">
                <ImageIcon aria-hidden="true" className="size-3.5" />
                {task.photoCount} photo{task.photoCount === 1 ? '' : 's'}
              </span>
            ) : null}
          </div>

          {task.isCompleted ? (
            <p className="mt-1.5 text-meta text-status-completed-text">
              Done
              {task.completedByName === null ? '' : ` by ${task.completedByName}`}
              {task.completedAt === null ? '' : ` · ${formatInstant(task.completedAt)}`}
            </p>
          ) : task.requiresPhoto ? (
            // The sentence that makes the rule discoverable rather than
            // surprising. It sits under every open photo-required task.
            <p className="mt-1.5 text-meta text-ink-500">
              Upload a photo to mark this task complete
            </p>
          ) : (
            <p className="mt-1.5 text-meta text-ink-500">
              No photo needed — the office ticks this one off.
            </p>
          )}

          {/*
            **The task does not go green while a photo is queued**, because the
            server is what completes it and the server has not seen the photo
            yet (business-rules.md §3.1). Saying "waiting to upload" is the
            truth, and it is also the thing that stops somebody driving away
            from a house with three photos still on the phone.
          */}
          {inFlight > 0 ? (
            <p className="mt-1.5 flex items-center gap-1.5 text-meta text-ink-700">
              <CloudUpload aria-hidden="true" className="size-3.5" />
              {inFlight} photo{inFlight === 1 ? '' : 's'} waiting to upload
            </p>
          ) : null}

          {stuck.length > 0 ? (
            <p className="mt-1.5 flex items-start gap-1.5 text-meta text-status-priority-text">
              <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
              {/* The server's own words, not "something went wrong". */}
              {stuck[0]?.lastError ?? 'That photo could not be uploaded.'}
            </p>
          ) : null}
        </div>
      </div>

      {canUpload ? (
        <div className="mt-3 pl-8">
          {/*
            44px minimum, `bark-700` on white at 8.3:1. The prototype's is
            99×30 at 1.34:1 and there are ten of them per job, tapped one-handed
            in a customer's house (P23, P24).
          */}
          <Button variant="outline" size="md" onClick={onAddPhoto}>
            <Camera aria-hidden="true" />
            {task.isCompleted ? 'Add another photo' : 'Add photo'}
          </Button>
        </div>
      ) : null}
    </li>
  );
}
